import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import {
  BOOKING_ISSUE_TYPES,
  SUPPORT_TICKET_SOURCES,
  createSupportTicket,
  groupSupportTicketsByTab,
  listMySupportTicketsPage,
  supportTicketSubject,
} from '../lib/supportTickets';
import BookingPagination from '../components/BookingPagination';
import { BusyTable } from '../components/TableBusyOverlay';
import usePagedListUi from '../hooks/usePagedListUi';

export default function HelpSupportPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ongoing');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [issueType, setIssueType] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const listTopRef = useRef(null);
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');

  const loadTickets = useCallback(async () => {
    setListError('');
    const { rows } = await listMySupportTicketsPage({ page: 1, pageSize: 100 });
    setTickets(rows);
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!user?.id && !localStorage.getItem('userAuthenticated')) {
          navigate('/login', { replace: true });
          return;
        }

        if (!user?.id) {
          navigate('/login', { replace: true });
          return;
        }

        await loadTickets();
      } catch (error) {
        setListError(error.message || 'Unable to load your support tickets.');
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, [loadTickets, navigate]);

  const grouped = useMemo(() => groupSupportTicketsByTab(tickets), [tickets]);
  const visibleTickets = grouped[activeTab] || [];
  const PAGE_SIZE = 5;
  const totalPages = Math.max(1, Math.ceil(visibleTickets.length / PAGE_SIZE));
  const pagedTickets = visibleTickets.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const { showSkeleton, goToPage } = usePagedListUi({
    currentPage,
    setCurrentPage,
    loading,
    scrollTargetRef: listTopRef,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const closeForm = () => {
    setIsFormOpen(false);
    setIssueType('');
    setReason('');
    setFormError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!issueType || !reason.trim()) {
      setFormError('Please select an issue type and describe the issue.');
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      await createSupportTicket({
        issueType,
        description: reason,
        source: SUPPORT_TICKET_SOURCES.BOOKING_APP,
      });
      await loadTickets();
      setActiveTab('ongoing');
      closeForm();
    } catch (error) {
      setFormError(error.message || 'Unable to submit this ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-[100dvh] overflow-hidden bg-[#141414] text-white px-5 py-4">
      <div className="mx-auto flex h-full max-w-sm flex-col overflow-x-hidden">
        <div className="relative flex items-center justify-center">
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className="absolute left-0 inline-flex h-10 w-10 items-center justify-center rounded-full text-white/90 transition hover:bg-white/5"
            aria-label="Go back"
          >
            <ArrowLeft size={22} />
          </button>

          <h1 className="text-[22px] font-semibold tracking-tight text-white">Help &amp; Support</h1>
        </div>

        <div className="mt-6 flex overflow-hidden rounded-full bg-white/10 p-1">
          <button
            type="button"
            onClick={() => setActiveTab('ongoing')}
            className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition ${
              activeTab === 'ongoing' ? 'bg-[#1268ff] text-white' : 'text-white/70'
            }`}
          >
            Ongoing
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('closed')}
            className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition ${
              activeTab === 'closed' ? 'bg-[#1268ff] text-white' : 'text-white/70'
            }`}
          >
            Closed
          </button>
        </div>

        <div ref={listTopRef} className="mt-5 min-h-0 flex-1 overflow-y-auto space-y-3 pr-0.5">
          {listError ? (
            <p className="rounded-[8px] bg-white/10 px-4 py-3 text-center text-xs leading-relaxed text-red-200">
              {listError}
            </p>
          ) : visibleTickets.length === 0 ? (
            <p className="py-8 text-center text-sm text-white/55">
              {loading || showSkeleton
                ? 'Loading...'
                : activeTab === 'ongoing'
                ? 'You have no ongoing support tickets.'
                : 'You have no closed support tickets.'}
            </p>
          ) : (
            <BusyTable show={showSkeleton} tone="public" className="space-y-3">
            {pagedTickets.map((ticket) => (
              <button
                key={ticket.id}
                type="button"
                onClick={() => setSelectedTicket(ticket)}
                className="flex w-full flex-col items-start rounded-[8px] bg-[#a9a9a9] px-4 py-3 text-left text-[#111]"
              >
                <span className="text-sm font-semibold">{ticket.ticket_code}</span>
                <span className="text-xs text-[#222]/70">{supportTicketSubject(ticket)}</span>
              </button>
            ))}
            </BusyTable>
          )}

          <BookingPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />

          <div className="pt-4 flex justify-center">
            <button
              type="button"
              onClick={() => {
                setFormError('');
                setIsFormOpen(true);
              }}
              className="inline-flex min-w-[170px] items-center justify-center rounded-full bg-[#1268ff] px-6 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(18,104,255,0.35)] transition hover:bg-[#0f5ee5]"
            >
              Submit Ticket
            </button>
          </div>
        </div>

        {isFormOpen && (
          <div className="fixed inset-0 z-50 overflow-x-hidden bg-black/65 px-5 py-8">
            <div className="mx-auto flex h-full w-full max-w-[390px] items-center justify-center">
              <form onSubmit={handleSubmit} className="relative w-full max-h-[90dvh] overflow-y-auto rounded-[12px] bg-[#e7e7e7] px-5 py-5 text-[#191919] shadow-2xl">
                <button
                  type="button"
                  onClick={closeForm}
                  className="absolute right-3 top-3 text-[#777] transition hover:text-[#111]"
                  aria-label="Close form"
                >
                  <X size={18} />
                </button>

                <h2 className="text-center text-[15px] font-semibold text-[#1a1a1a]">Submit New Support Ticket</h2>
                <p className="mt-6 text-center text-[10px] text-[#8f8f8f]">
                  Please select the issue you need assistance with.
                </p>

                <label className="mt-4 block text-[11px] font-semibold text-[#222]">Select Issue Type</label>
                <select
                  value={issueType}
                  onChange={(e) => setIssueType(e.target.value)}
                  className="mt-2 w-full rounded-[5px] border border-[#d2d2d2] bg-white px-3 py-2 text-[11px] text-[#444] outline-none"
                >
                  <option value="">Select the issue...</option>
                  {BOOKING_ISSUE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>

                <label className="mt-4 block text-[11px] font-semibold text-[#222]">Reason/Details (required)</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={8}
                  placeholder="Please briefly explain the issue and what correction is needed."
                  className="mt-2 w-full resize-none rounded-[5px] border border-[#d2d2d2] bg-white px-3 py-2 text-[11px] text-[#444] outline-none"
                />

                {formError ? (
                  <p className="mt-3 text-center text-[11px] leading-relaxed text-red-700">{formError}</p>
                ) : null}

                <button
                  type="submit"
                  disabled={submitting}
                  className="mx-auto mt-5 block rounded-[7px] bg-[#0c8f14] px-6 py-2 text-[11px] font-semibold text-white transition hover:bg-[#08730f] disabled:opacity-60"
                >
                  {submitting ? 'Submitting...' : 'Submit Ticket'}
                </button>
              </form>
            </div>
          </div>
        )}

        {selectedTicket && (
          <div className="fixed inset-0 z-50 overflow-x-hidden bg-black/65 px-5 py-8">
            <div className="mx-auto flex h-full w-full max-w-[390px] items-center justify-center">
              <div className="relative w-full max-h-[90dvh] overflow-y-auto rounded-[12px] bg-[#e7e7e7] px-5 py-5 text-[#191919] shadow-2xl">
                <button
                  type="button"
                  onClick={() => setSelectedTicket(null)}
                  className="absolute right-3 top-3 text-[#777] transition hover:text-[#111]"
                  aria-label="Close ticket details"
                >
                  <X size={18} />
                </button>

                <h2 className="pr-6 text-center text-[15px] font-semibold text-[#1a1a1a]">Ticket details</h2>

                <dl className="mt-5 space-y-3 text-[12px]">
                  <div>
                    <dt className="font-semibold text-[#222]">Ticket code</dt>
                    <dd className="mt-1 text-[#444]">{selectedTicket.ticket_code}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-[#222]">Issue type</dt>
                    <dd className="mt-1 text-[#444]">{selectedTicket.issue_type}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-[#222]">Status</dt>
                    <dd className="mt-1 text-[#444]">{selectedTicket.status}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-[#222]">Details</dt>
                    <dd className="mt-1 whitespace-pre-wrap break-words text-[#444]">{selectedTicket.description}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
